import {
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { Field as BaseField } from "@base-ui/react/field";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import styles from "./Field.module.scss";
import type {
    FieldControlProps,
    FieldDescriptionProps,
    FieldErrorProps,
    FieldLabelProps,
    FieldMessageProps,
    FieldRootProps,
} from "./Field.types";

export const FieldRoot = ({ className, ref, ...props }: FieldRootProps) => (
    <BaseField.Root
        ref={ref}
        className={classNames(styles.root, className)}
        {...props}
    />
);

export const FieldLabel = ({ className, ref, ...props }: FieldLabelProps) => (
    <BaseField.Label
        ref={ref}
        className={classNames(styles.label, className)}
        {...props}
    />
);

export const FieldDescription = ({
    className,
    ref,
    ...props
}: FieldDescriptionProps) => (
    <BaseField.Description
        ref={ref}
        className={classNames(styles.message, className)}
        {...props}
    />
);

export const FieldError = ({ className, ref, ...props }: FieldErrorProps) => (
    <BaseField.Error
        ref={ref}
        className={classNames(styles.message, styles.error, className)}
        {...props}
    />
);

const hasContent = (node: ReactNode) => node !== undefined && node !== null;

/**
 * The line under a control: the error when there is one, else the
 * description. The slot stays mounted so the error animates in, and the last
 * error text stays (hidden from assistive tech) until its fade-out finishes.
 * Motion lives in Field.module.scss.
 */
export const FieldMessage = ({ error, description }: FieldMessageProps) => {
    const hasError = hasContent(error);
    const hasDescription = hasContent(description);
    const [lastError, setLastError] = useState<ReactNode>(null);
    if (hasError && error !== lastError) {
        setLastError(error);
    }
    if (!hasError && hasDescription && lastError !== null) {
        setLastError(null);
    }

    const exitLineRef = useRef<HTMLDivElement>(null);
    const isExiting = !hasError && !hasDescription && lastError !== null;
    useEffect(() => {
        const exitLine = exitLineRef.current;
        if (!isExiting || exitLine === null) {
            return;
        }
        let isCurrent = true;
        // jsdom has no Web Animations API.
        const animations =
            "getAnimations" in exitLine ? exitLine.getAnimations() : [];
        Promise.all(animations.map((animation) => animation.finished)).then(
            () => {
                if (isCurrent) {
                    setLastError(null);
                }
            },
            () => {},
        );
        return () => {
            isCurrent = false;
        };
    }, [isExiting]);

    let line: ReactNode = null;
    if (hasError) {
        line = (
            <FieldError
                match
                className={classNames(styles.messageLine, styles.errorEnter)}
            >
                {error}
            </FieldError>
        );
    } else if (hasDescription) {
        line = (
            <FieldDescription className={styles.messageLine}>
                {description}
            </FieldDescription>
        );
    } else if (isExiting) {
        line = (
            <div
                ref={exitLineRef}
                aria-hidden
                className={classNames(
                    styles.message,
                    styles.error,
                    styles.messageLine,
                    styles.errorExit,
                )}
            >
                {lastError}
            </div>
        );
    }

    return (
        <div
            className={styles.messageSlot}
            data-open={hasError || hasDescription || undefined}
        >
            <div className={styles.messageClip}>{line}</div>
        </div>
    );
};

/**
 * Base UI controls register with Field automatically. Custom controls use
 * render and forward its generated id and aria props to their focusable node.
 */
export const FieldControl = ({
    className,
    ref,
    render,
    required,
    ...props
}: FieldControlProps) => {
    const assignRef = useCallback(
        (node: HTMLElement | null) => {
            const input = node instanceof HTMLInputElement ? node : null;
            mergeRefs([ref])(input);
        },
        [ref],
    );
    const renderWithAriaRequired: FieldControlProps["render"] =
        typeof render === "function"
            ? (controlProps, state) =>
                  render(
                      {
                          ...controlProps,
                          "aria-required":
                              controlProps["aria-required"] ??
                              (required || undefined),
                      },
                      state,
                  )
            : render;

    return (
        <BaseField.Control
            ref={assignRef}
            className={className}
            render={renderWithAriaRequired}
            required={required}
            {...props}
        />
    );
};
